import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./styles/CablingSolutions.css";

const cablingTabs = [
  {
    key: "environments",
    label: "Environments",
    title: "Cabling support across office, business, and industrial environments",
    text: "ComNetworks provides cabling installation services for voice, video, and data networks across small and medium local businesses as well as large industrial facilities, with solutions shaped around the environment and scale of the site.",
    items: [
      "Small and medium local business environments",
      "Large industrial facilities",
      "Voice, video, and data network support",
    ],
  },
  {
    key: "buildouts",
    label: "Build Outs",
    title: "Structured build outs for data centers and network spaces",
    text: "From networking fundamentals to data center build outs, relay rack installations, and server cabinet setups, the service supports both foundational infrastructure and expansion work.",
    items: [
      "Networking fundamentals",
      "Data center build outs",
      "Relay rack and server cabinet installations",
    ],
  },
  {
    key: "pathways",
    label: "Pathways",
    title: "Organized pathways and management systems for cleaner cabling",
    text: "Cable trays, ladder racks, and cable management systems help keep installations more organized, scalable, and easier to maintain over time.",
    items: [
      "Cable trays and ladder racks",
      "Cable management systems",
      "Pathway planning for growth and maintenance",
    ],
  },
  {
    key: "changes",
    label: "Moves / Adds / Changes",
    title: "Support for new construction and day to day cabling changes",
    text: "ComNetworks supports new construction work as well as move, add, or change cabling projects where systems need to evolve without rebuilding everything from scratch.",
    items: [
      "New construction cabling",
      "Move, add, or change (MAC) support",
      "Flexible updates to the installed environment",
    ],
  },
];

const pathwaySteps = [
  {
    no: "01",
    title: "Plan the route",
    text: "Understand site layout, endpoints, rack locations, and installation pathways before pulling cable.",
  },
  {
    no: "02",
    title: "Build the support structure",
    text: "Set up trays, ladder racks, cabinets, relay racks, and physical cable pathways that keep the install organized.",
  },
  {
    no: "03",
    title: "Install and manage cable",
    text: "Run voice, video, and data cabling with cleaner routing, better separation, and more maintainable cable management.",
  },
  {
    no: "04",
    title: "Test and certify",
    text: "Verify installed cable performance and help confirm the environment is ready for reliable operation.",
  },
];

const mediaCards = [
  {
    title: "Cat5e",
    text: "Support practical copper network installations where standard structured cabling is appropriate for the environment.",
  },
  {
    title: "Cat6",
    text: "Deploy higher performance copper cabling for modern business and network infrastructure requirements.",
  },
  {
    title: "Single mode fiber",
    text: "Use fiber where distance, backbone connectivity, or higher capacity transmission requirements are important.",
  },
  {
    title: "Multi mode fiber",
    text: "Support high speed data communication across shorter distance fiber environments inside facilities or campuses.",
  },
  {
    title: "Coaxial cable",
    text: "Install coaxial cable where video, specialized systems, or legacy signal requirements still play a role.",
  },
];

const boardGroups = [
  {
    title: "Rack and cabinet work",
    items: [
      "Relay rack installations",
      "Server cabinet installations",
      "Rack ready layout for patching and growth",
    ],
  },
  {
    title: "Pathway systems",
    items: [
      "Cable trays and ladder racks",
      "Organized horizontal and vertical routing",
      "Support for cleaner maintenance and expansion",
    ],
  },
  {
    title: "Cabling operations",
    items: [
      "New construction cabling",
      "Move, add, or change cabling",
      "Installation and certification support",
    ],
  },
];

const fitCards = [
  {
    title: "Businesses building or upgrading sites",
    text: "For companies setting up new offices, expanding sites, or modernizing network cabling infrastructure.",
  },
  {
    title: "Data center and server room projects",
    text: "For teams planning build outs, rack installations, cabinet work, or more organized cable pathways.",
  },
  {
    title: "Industrial and mixed use environments",
    text: "For facilities that need cabling solutions adapted to larger, more complex operational spaces.",
  },
  {
    title: "Teams needing structured MAC support",
    text: "For businesses that regularly move, add, or change endpoints, equipment, and physical network connections.",
  },
];

export default function CablingSolutions() {
  const [activeKey, setActiveKey] = useState("environments");
  const activeTab =
    cablingTabs.find((item) => item.key === activeKey) || cablingTabs[0];

  return (
    <div className="cablePage">
      <section className="cableHero">
        <div className="cableContainer cableHero__container">
          <div className="cableHero__content">
            <p className="cableEyebrow">Cabling Solutions</p>

            <h1 className="cableHero__title">
              Structured cabling for cleaner pathways, stronger layouts, and scalable installations.
            </h1>

            <p className="cableHero__text">
              ComNetworks provides cabling installation services for voice,
              video, and data networks across environments ranging from small
              and medium local businesses to large industrial facilities, with
              support for build outs, racks, pathways, cable management, MAC work,
              and copper, fiber, and coaxial cable installation and certification.
            </p>

            <div className="cableHero__actions">
              <Link to="/contact" className="cableBtn cableBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="cableBtn cableBtn--secondary">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="cableHero__visual">
            <div className="cableRouteBoard">
              <div className="cableRouteBoard__top">
                <span className="cableTag">Structured pathways</span>
                <span className="cableTag cableTag--soft">Install + certify</span>
              </div>

              <div className="cableRouteBoard__map">
                <div className="cableRouteNode cableRouteNode--one">Rack</div>
                <div className="cableRouteNode cableRouteNode--two">Tray</div>
                <div className="cableRouteNode cableRouteNode--three">Cabinet</div>
                <div className="cableRouteNode cableRouteNode--four">Endpoint</div>

                <span className="cableRouteLine cableRouteLine--one" />
                <span className="cableRouteLine cableRouteLine--two" />
                <span className="cableRouteLine cableRouteLine--three" />
              </div>

              <div className="cableRouteBoard__bottom">
                <div className="cableInfoCard">
                  <strong>Physical organization</strong>
                  <span>Pathways and equipment laid out for clarity and growth</span>
                </div>
                <div className="cableInfoCard">
                  <strong>Installed and certified</strong>
                  <span>Support from cable routing through validation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cableTabsSection">
        <div className="cableContainer">
          <div className="cableSectionHead cableSectionHead--center">
            <p className="cableEyebrow">Service scope</p>
            <h2 className="cableTitle">
              A different tab style: pill based service navigation
            </h2>
            <p className="cableText">
              This page avoids the older tab patterns and uses rounded pill
              controls with a split content area to keep the design distinct.
            </p>
          </div>

          <div className="cableTabsShell">
            <div className="cableTabPills">
              {cablingTabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`cableTabPill ${activeKey === item.key ? "active" : ""}`}
                  onClick={() => setActiveKey(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="cableTabPanel">
              <div className="cableTabPanel__text">
                <p className="cableTabPanel__label">{activeTab.label}</p>
                <h3>{activeTab.title}</h3>
                <p>{activeTab.text}</p>
              </div>

              <div className="cableTabPanel__items">
                {activeTab.items.map((item) => (
                  <div key={item} className="cableMiniBox">
                    <span className="cableMiniBox__mark" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cablePathSection">
        <div className="cableContainer">
          <div className="cableSectionHead">
            <p className="cableEyebrow">Installation flow</p>
            <h2 className="cableTitle">
              A pathway style section that follows how physical cabling work happens
            </h2>
            <p className="cableText">
              This section is intentionally laid out like a sequence rather than
              another card grid so the page feels different from the earlier service pages.
            </p>
          </div>

          <div className="cablePathGrid">
            {pathwaySteps.map((item) => (
              <article key={item.no} className="cableStepCard">
                <span className="cableStepCard__no">{item.no}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cableMediaSection">
        <div className="cableContainer">
          <div className="cableSectionHead cableSectionHead--center">
            <p className="cableEyebrow">Cable types</p>
            <h2 className="cableTitle">
              Media options presented as installation tiles instead of standard cards
            </h2>
            <p className="cableText">
              Cat5e, Cat6, fiber, and coax are shown in a more compact tile system
              to create another visual change in the page layout.
            </p>
          </div>

          <div className="cableMediaGrid">
            {mediaCards.map((item) => (
              <article key={item.title} className="cableMediaTile">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cableBoardsSection">
        <div className="cableContainer">
          <div className="cableBoardsWrap">
            {boardGroups.map((group) => (
              <article key={group.title} className="cableBoardCard">
                <h3>{group.title}</h3>
                <div className="cableBoardItems">
                  {group.items.map((item) => (
                    <div key={item} className="cableBoardItem">
                      <span className="cableBoardItem__icon" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cableCertSection">
        <div className="cableContainer">
          <div className="cableCertPanel">
            <div className="cableCertPanel__content">
              <p className="cableEyebrow">Certification focus</p>
              <h2 className="cableTitle">
                Installation quality matters more when the system is built to be maintained and verified
              </h2>
              <p className="cableText">
                Good cabling work is not only about pulling cable. It also depends
                on pathway organization, rack and cabinet planning, media choice,
                and certification that supports reliable long term use.
              </p>
            </div>

            <div className="cableCertPanel__numbers">
              <div className="cableNumberBox">
                <strong>Voice</strong>
                <span>Support cabling layouts for communications environments</span>
              </div>
              <div className="cableNumberBox">
                <strong>Video</strong>
                <span>Install media pathways for connected video requirements</span>
              </div>
              <div className="cableNumberBox">
                <strong>Data</strong>
                <span>Build structured network connectivity with certification support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cableFitSection">
        <div className="cableContainer">
          <div className="cableSectionHead cableSectionHead--center">
            <p className="cableEyebrow">Best fit</p>
            <h2 className="cableTitle">
              Where Cabling Solutions create the most value
            </h2>
            <p className="cableText">
              These services fit organizations that need physical network
              infrastructure installed, expanded, reorganized, or certified more effectively.
            </p>
          </div>

          <div className="cableFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="cableFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cableCta">
        <div className="cableContainer cableCta__container">
          <div className="cableCta__content">
            <p className="cableEyebrow cableEyebrow--light">Next step</p>
            <h2 className="cableCta__title">
              Plan a cleaner, better managed cabling environment for your site
            </h2>
            <p className="cableCta__text">
              If you need structured cabling for new construction, build outs,
              rack installations, pathway systems, MAC work, or cable certification,
              ComNetworks can help shape the right installation approach.
            </p>
          </div>

          <div className="cableCta__actions">
            <Link to="/contact" className="cableBtn cableBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="cableBtn cableBtn--ghost">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
